<template>
  <div
    class="fixed inset-0 z-[105] flex items-center justify-center bg-ink/50 px-4 py-6"
  >
    <div ref="dialogPanel" role="dialog" aria-modal="true" :aria-labelledby="dialogTitleId" tabindex="-1" @keydown="onDialogKeydown" class="w-full max-w-md flex flex-col paper-card rounded-md overflow-hidden">

      <!-- Header -->
      <div class="flex items-center gap-2 p-3.5 border-b border-[hsl(var(--border)/0.6)] shrink-0">
        <div class="min-w-0 flex-1">
          <p :id="dialogTitleId" class="text-[15px] font-semibold truncate">
            {{ mode === 'onboarding' ? $t('editProfile.onboardingTitle') : $t('editProfile.editTitle') }}
          </p>
          <p v-if="mode === 'onboarding'" class="text-[11px] text-ink2 truncate">
            {{ $t('editProfile.onboardingSubtitle') }}
          </p>
        </div>
        <button
          v-if="mode === 'edit'"
          @click="$emit('close')"
          class="shrink-0 w-8 h-8 rounded-full flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors"
          :aria-label="$t('liveHome.close')"
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <div v-if="error" class="mx-4 mt-3 flex items-start gap-2 px-3 py-2 rounded-md bg-[hsl(var(--danger)/0.08)] text-[hsl(var(--danger))] shrink-0">
        <CircleAlert class="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <p class="text-[12px] leading-snug">{{ error }}</p>
      </div>

      <!-- Body -->
      <div class="overflow-y-auto px-4 py-3.5 space-y-3.5 max-h-[70vh]">

        <!-- Avatar -->
        <div class="flex flex-col items-center gap-2">
          <div class="relative">
            <button
              type="button"
              @click="fileInput?.click()"
              class="w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-br from-[hsl(var(--primary)/0.3)] to-[hsl(var(--primary)/0.1)] border-2 border-[hsl(var(--primary)/0.2)] overflow-hidden shrink-0"
              :aria-label="$t('editProfile.avatarChange')"
            >
              <img v-if="auth.avatarUrl" :src="auth.avatarUrl" alt="" class="w-full h-full object-cover" />
              <span v-else class="text-[26px] font-semibold text-[hsl(var(--primary))]">
                {{ username.trim() ? username.trim()[0].toUpperCase() : "?" }}
              </span>
              <div v-if="avatarUploading || avatarPending" class="absolute inset-0 bg-ink/50 flex items-center justify-center">
                <Loader2 class="w-5 h-5 animate-spin text-white" />
              </div>
            </button>
            <button
              type="button"
              @click="fileInput?.click()"
              class="absolute -bottom-0.5 -right-0.5 w-7 h-7 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center shadow-ink-sm hover:bg-[hsl(158,38%,30%)] transition-colors"
              :aria-label="$t('editProfile.avatarChange')"
            >
              <Camera class="w-3.5 h-3.5" />
            </button>
            <button
              v-if="auth.avatarUrl"
              type="button"
              @click="removeAvatar"
              class="absolute -bottom-0.5 -left-0.5 w-7 h-7 rounded-full bg-sheet border border-border text-[hsl(var(--danger))] flex items-center justify-center shadow-ink-sm hover:bg-red-50 transition-colors"
              :aria-label="$t('editProfile.avatarRemove')"
            >
              <Trash2 class="w-3.5 h-3.5" />
            </button>
          </div>
          <input ref="fileInput" type="file" accept="image/*" class="hidden" @change="onAvatarChange" />
          <p v-if="avatarPending" class="text-[11px] text-ink2">{{ $t('editProfile.avatarLoadingFromProvider') }}</p>
        </div>

        <label class="flex flex-col gap-1">
          <span class="text-[11px] font-semibold text-ink2">{{ $t('editProfile.username') }}</span>
          <div class="relative">
            <span class="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-ink2">@</span>
            <input
              v-model="username"
              type="text"
              autocomplete="off"
              class="w-full h-10 pl-6 pr-3 rounded-md border text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] transition"
              :class="usernameError ? 'border-[hsl(var(--danger))] bg-[hsl(var(--danger)/0.05)]' : 'border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)] focus:border-[hsl(var(--primary)/0.5)]'"
              @input="usernameTaken = false"
            />
          </div>
          <span v-if="usernameError" class="text-[11px] text-[hsl(var(--danger))]">{{ usernameError }}</span>
          <span v-else-if="usernameTaken" class="text-[11px] text-[hsl(var(--danger))]">{{ $t('editProfile.usernameTaken') }}</span>
        </label>

        <div class="grid grid-cols-2 gap-2">
          <label class="flex flex-col gap-1">
            <span class="text-[11px] font-semibold text-ink2">{{ $t('editProfile.firstName') }}</span>
            <input
              v-model="firstName"
              type="text"
              autocomplete="given-name"
              class="w-full h-10 px-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-[11px] font-semibold text-ink2">{{ $t('editProfile.lastName') }}</span>
            <input
              v-model="lastName"
              type="text"
              autocomplete="family-name"
              class="w-full h-10 px-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition"
            />
          </label>
        </div>

        <div class="grid grid-cols-2 gap-2">
          <label class="flex flex-col gap-1">
            <span class="text-[11px] font-semibold text-ink2">{{ $t('editProfile.gender') }}</span>
            <div class="relative rounded-md border border-border bg-[hsl(var(--muted)/0.4)]">
              <select
                v-model="gender"
                class="w-full h-10 pl-3 pr-6 bg-transparent text-[13px] outline-none appearance-none rounded-md cursor-pointer"
              >
                <option value="">{{ $t('editProfile.genderUnset') }}</option>
                <option value="male">{{ $t('editProfile.genderMale') }}</option>
                <option value="female">{{ $t('editProfile.genderFemale') }}</option>
                <option value="other">{{ $t('editProfile.genderOther') }}</option>
              </select>
              <ChevronDown class="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink2 pointer-events-none" />
            </div>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-[11px] font-semibold text-ink2">{{ $t('editProfile.birthDate') }}</span>
            <input
              v-model="birthDate"
              type="date"
              class="w-full h-10 px-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:transition-opacity [&::-webkit-calendar-picker-indicator]:hover:opacity-90"
            />
          </label>
        </div>

        <div class="flex items-center justify-between gap-3 p-3 rounded-md bg-[hsl(var(--muted)/0.4)]">
          <div class="min-w-0">
            <p class="text-[12.5px] font-semibold">{{ $t('editProfile.visibility') }}</p>
            <p class="text-[11px] text-ink2">{{ $t('editProfile.visibilityHint') }}</p>
          </div>
          <div class="flex rounded-full border border-border bg-sheet p-0.5 shrink-0">
            <button
              type="button"
              @click="profileVisibility = 'private'"
              class="h-7 px-2.5 rounded-full text-[11px] font-semibold transition-colors"
              :class="profileVisibility === 'private' ? 'bg-[hsl(var(--primary))] text-white' : 'text-ink2'"
            >{{ $t('editProfile.visibilityPrivate') }}</button>
            <button
              type="button"
              @click="profileVisibility = 'public'"
              class="h-7 px-2.5 rounded-full text-[11px] font-semibold transition-colors"
              :class="profileVisibility === 'public' ? 'bg-[hsl(var(--primary))] text-white' : 'text-ink2'"
            >{{ $t('editProfile.visibilityPublic') }}</button>
          </div>
        </div>

        <div v-if="mode === 'onboarding'" class="pt-1">
          <label class="flex items-start gap-2 cursor-pointer">
            <input
              v-model="termsAccepted"
              type="checkbox"
              class="mt-0.5 w-4 h-4 shrink-0 rounded border-[hsl(var(--border))] text-[hsl(var(--primary))] focus:ring-[hsl(var(--primary)/0.3)]"
            />
            <span class="text-[12px] leading-snug text-[hsl(var(--foreground)/0.85)]">
              {{ $t('editProfile.consentText') }}
            </span>
          </label>
          <div class="mt-1.5 pl-6 flex flex-wrap gap-x-3 gap-y-1">
            <button type="button" @click.prevent="showTermsOfService = true" class="text-[11px] text-[hsl(var(--primary))] font-semibold underline underline-offset-2">
              {{ $t('aboutProject.documents.termsOfService') }}
            </button>
            <button type="button" @click.prevent="showPrivacyPolicy = true" class="text-[11px] text-[hsl(var(--primary))] font-semibold underline underline-offset-2">
              {{ $t('aboutProject.documents.privacyPolicy') }}
            </button>
            <button type="button" @click.prevent="showTerms = true" class="text-[11px] text-[hsl(var(--primary))] font-semibold underline underline-offset-2">
              {{ $t('aboutProject.documents.dataConsent') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center gap-2 px-4 py-3 border-t border-[hsl(var(--border)/0.6)] shrink-0">
        <button
          v-if="mode === 'edit'"
          type="button"
          @click="$emit('close')"
          class="h-10 px-3 rounded-md bg-[hsl(var(--muted))] text-[12.5px] font-semibold hover:bg-[hsl(var(--muted)/0.8)] transition-colors"
        >
          {{ $t('routeRegistration.form.cancel') }}
        </button>
        <button
          type="button"
          @click="submit"
          :disabled="!canSubmit || submitting"
          class="flex-1 h-10 rounded-md bg-[hsl(var(--primary))] text-white text-[13px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
        >
          <Loader2 v-if="submitting" class="w-4 h-4 animate-spin" />
          {{ mode === 'onboarding' ? $t('editProfile.submitOnboarding') : $t('editProfile.submitEdit') }}
        </button>
      </div>
    </div>

    <LegalDocumentModal v-if="showTerms" :document="dataConsent" @close="showTerms = false" />
    <LegalDocumentModal v-if="showTermsOfService" :document="termsOfService" @close="showTermsOfService = false" />
    <LegalDocumentModal v-if="showPrivacyPolicy" :document="privacyPolicy" @close="showPrivacyPolicy = false" />
    <AvatarCropDialog
      v-if="pendingCropFile"
      :file="pendingCropFile"
      @cancel="pendingCropFile = null"
      @cropped="onCropped"
    />
  </div>
</template>

<script setup lang="ts">
import { useDialogA11y } from "../composables/useDialogA11y";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { X, Camera, Trash2, ChevronDown, CircleAlert, Loader2 } from "lucide-vue-next";
import api from "../api";
import { useAuthStore } from "../store/auth";
import { USERNAME_PATTERN, RESERVED_USERNAMES } from "../constants/reservedUsernames";
import { dataConsent, termsOfService, privacyPolicy } from "../content/legalDocuments";
import LegalDocumentModal from "./LegalDocumentModal.vue";
import AvatarCropDialog from "./AvatarCropDialog.vue";
import { useHistoryBackClose } from "../composables/useHistoryBackClose";

const props = defineProps<{
  mode: "onboarding" | "edit";
}>();

const emit = defineEmits<{
  (e: "close"): void;
}>();

// Кнопка "назад" в браузере закрывает диалог редактирования. В режиме
// onboarding он намеренно неотменяемый - там перехват не ставим (mode
// не меняется в течение жизни инстанса, условный вызов безопасен).
if (props.mode === "edit") {
  useHistoryBackClose(() => emit("close"));
}

const { t } = useI18n();
const auth = useAuthStore();

const username = ref(auth.username);
// Пользователи по привычке вводят "@" (как в Telegram/Instagram/X), но у
// поля уже есть декоративный префикс "@" - введённый или вставленный
// символ убираем, а не показываем ошибку формата.
watch(username, (v) => {
  if (v.startsWith("@")) username.value = v.slice(1);
});
const firstName = ref(auth.firstName);
const lastName = ref(auth.lastName);
const gender = ref(auth.gender);
const birthDate = ref(auth.birthDate);
const profileVisibility = ref<"private" | "public">(
  auth.profileVisibility === "public" ? "public" : "private"
);
const termsAccepted = ref(false);
const showTerms = ref(false);
const showTermsOfService = ref(false);
const showPrivacyPolicy = ref(false);
const submitting = ref(false);
const usernameTaken = ref(false);
const error = ref("");

const fileInput = ref<HTMLInputElement | null>(null);
const avatarUploading = ref(false);
const pendingCropFile = ref<File | null>(null);
const avatarPending = ref(false);
let avatarPollTimer: number | null = null;

const usernameError = computed(() => {
  const v = username.value.trim().toLowerCase();
  if (!v) return t("editProfile.usernameRequired");
  if (!USERNAME_PATTERN.test(v)) return t("editProfile.usernameFormat");
  if (RESERVED_USERNAMES.has(v)) return t("editProfile.usernameReserved");
  return "";
});

const canSubmit = computed(() => {
  if (usernameError.value) return false;
  if (props.mode === "onboarding" && !termsAccepted.value) return false;
  return true;
});

// Ручная загрузка всегда важнее ещё не завершённого запроса аватара из
// Telegram - опрос останавливается, как только пользователь сам вмешался.
const stopAvatarPolling = () => {
  avatarPending.value = false;
  if (avatarPollTimer) {
    window.clearTimeout(avatarPollTimer);
    avatarPollTimer = null;
  }
};

const onAvatarChange = (evt: Event) => {
  const file = (evt.target as HTMLInputElement).files?.[0];
  if (fileInput.value) fileInput.value.value = "";
  if (!file) return;
  stopAvatarPolling();
  pendingCropFile.value = file;
};

const uploadAvatarBlob = async (blob: Blob) => {
  avatarUploading.value = true;
  error.value = "";
  try {
    const formData = new FormData();
    formData.append("file", blob, "avatar.jpg");
    const res = await api.post("/auth/me/avatar", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    auth.setProfile(res.data);
  } catch {
    error.value = t("editProfile.avatarError");
  } finally {
    avatarUploading.value = false;
  }
};

const onCropped = (blob: Blob) => {
  pendingCropFile.value = null;
  uploadAvatarBlob(blob);
};

const removeAvatar = async () => {
  stopAvatarPolling();
  avatarUploading.value = true;
  try {
    const res = await api.delete("/auth/me/avatar");
    auth.setProfile(res.data);
  } catch {
    error.value = t("editProfile.avatarError");
  } finally {
    avatarUploading.value = false;
  }
};

// Онбординг может открыться раньше, чем завершится фоновая загрузка
// аватара из Google/Telegram на бэкенде (service.py::_spawn_avatar_copy) -
// логин намеренно её не ждёт. Опрашиваем несколько раз, чтобы аватар
// успел подтянуться без переоткрытия диалога; молча сдаёмся, если
// подтягивать нечего или он так и не появился.
const AVATAR_POLL_INTERVAL_MS = 1500;
const AVATAR_POLL_MAX_ATTEMPTS = 6;

const startAvatarPolling = () => {
  if (props.mode !== "onboarding") return;
  if (auth.avatarUrl) return;
  const hasOAuthAvatarSource = auth.identities.some((i) => i.provider === "telegram" || i.provider === "google");
  if (!hasOAuthAvatarSource) return;

  avatarPending.value = true;
  let attempts = 0;
  const poll = async () => {
    attempts++;
    try {
      const res = await api.get("/auth/me");
      auth.setProfile(res.data);
    } catch {
      // пробуем дальше, пока не кончатся попытки - временный сбой не повод сдаваться сразу
    }
    if (auth.avatarUrl || attempts >= AVATAR_POLL_MAX_ATTEMPTS) {
      avatarPending.value = false;
      return;
    }
    avatarPollTimer = window.setTimeout(poll, AVATAR_POLL_INTERVAL_MS);
  };
  avatarPollTimer = window.setTimeout(poll, AVATAR_POLL_INTERVAL_MS);
};

const submit = async () => {
  if (!canSubmit.value || submitting.value) return;
  submitting.value = true;
  error.value = "";
  usernameTaken.value = false;
  try {
    const res = await api.patch("/auth/me/profile", {
      username: username.value.trim().toLowerCase(),
      first_name: firstName.value.trim() || null,
      last_name: lastName.value.trim() || null,
      gender: gender.value || null,
      birth_date: birthDate.value || null,
      profile_visibility: profileVisibility.value,
      terms_accepted: props.mode === "onboarding" ? true : undefined,
      privacy_policy_version: props.mode === "onboarding" ? privacyPolicy.version : undefined,
      terms_of_service_version: props.mode === "onboarding" ? termsOfService.version : undefined,
      data_consent_version: props.mode === "onboarding" ? dataConsent.version : undefined,
    });
    auth.setProfile(res.data);
    if (props.mode === "edit") emit("close");
    // в режиме онбординга явное закрытие не нужно - гейт родителя реактивно
    // считается от auth.termsAcceptedAt, который только что выставил setProfile()
  } catch (err: any) {
    if (err?.response?.status === 409) {
      usernameTaken.value = true;
    } else {
      error.value = t("editProfile.saveError");
    }
  } finally {
    submitting.value = false;
  }
};

onMounted(() => {
  // Онбординг может показаться сразу после логина раньше, чем вызывающий
  // код успеет выставить auth.* - досинхронизируем после mount, чтобы
  // форма не стартовала пустой при уже заполненном сторе.
  username.value = auth.username;
  firstName.value = auth.firstName;
  lastName.value = auth.lastName;
  gender.value = auth.gender;
  birthDate.value = auth.birthDate;
  startAvatarPolling();
});

onBeforeUnmount(() => {
  stopAvatarPolling();
});

// Доступность окна: ловушка фокуса, Esc, возврат фокуса (useDialogA11y).
const dialogPanel = ref<HTMLElement | null>(null);
const { titleId: dialogTitleId, onKeydown: onDialogKeydown } = useDialogA11y(dialogPanel, { onClose: () => emit('close'), dismissible: () => props.mode === 'edit' });
</script>
